import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Chip,
  Input,
} from '@nextui-org/react';
import {
  getCaseExternalEntities,
  saveCaseExternalEntity,
  type CaseExternalEntity,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, LoadingState } from '../../components/ui';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';

interface Props {
  exporterId: number;
  identity: string;
  selected?: number | null;
  onSelectionChange?: (id: number | null) => void;
  onReadyChange?: (ready: boolean) => void;
  disabled?: boolean;
}
export default function CaseExternalEntities(props: Props) {
  return (
    <EntityCatalog key={`${props.identity}:${props.exporterId}`} {...props} />
  );
}
function EntityCatalog({
  exporterId,
  identity,
  selected,
  onSelectionChange,
  onReadyChange,
  disabled,
}: Props) {
  const { t } = useTranslation('shipmentCases');
  const load = useCallback(
    (signal: AbortSignal) => getCaseExternalEntities(exporterId, signal),
    [exporterId]
  );
  const request = useScopedRequest(
    `${identity}:external-entities:${exporterId}`,
    load
  );
  const [managing, setManaging] = useState(selected === undefined);
  const [editing, setEditing] = useState<CaseExternalEntity | undefined>();
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const entities = request.data?.entities || [];
  const active = entities.filter((e) => e.active);
  const frozen = disabled || saving;
  const ready =
    !request.loading &&
    !request.error &&
    !saving &&
    active.some((e) => e.id === selected);
  useEffect(() => {
    onReadyChange?.(ready);
  }, [onReadyChange, ready]);
  useEffect(() => {
    if (
      !request.loading &&
      !request.error &&
      request.data &&
      selected != null &&
      !request.data.entities.some((e) => e.id === selected && e.active)
    )
      onSelectionChange?.(null);
  }, [
    request.loading,
    request.error,
    request.data,
    selected,
    onSelectionChange,
  ]);
  async function save(
    nameValue: string,
    typeValue: string,
    existing?: CaseExternalEntity,
    activeValue = true
  ) {
    if (pending.current || disabled) return;
    pending.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await saveCaseExternalEntity(
        exporterId,
        nameValue.trim(),
        typeValue.trim(),
        existing,
        activeValue
      );
      if (!alive.current) return;
      if (selected !== undefined && (!existing || selected === existing.id))
        onSelectionChange?.(result.active ? result.id : null);
      setEditing(undefined);
      setName('');
      setEntityType('');
      await request.reload();
    } catch (e) {
      if (alive.current) {
        setError(
          e instanceof Error
            ? e.message
            : String(
                (e as { message?: string })?.message ||
                  t('No se pudo guardar la entidad.')
              )
        );
        await request.reload();
      }
    } finally {
      pending.current = false;
      if (alive.current) setSaving(false);
    }
  }
  return (
    <section
      className='flex flex-col gap-3'
      aria-label={t('Entidades externas')}
    >
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p className='font-semibold'>{t('Entidades externas')}</p>
        {selected !== undefined && (
          <Button
            size='sm'
            variant='flat'
            isDisabled={frozen}
            onPress={() => setManaging(!managing)}
          >
            {t(managing ? 'Ocultar administración' : 'Administrar entidades')}
          </Button>
        )}
      </div>
      {request.loading ? (
        <LoadingState label={t('Cargando entidades…')} />
      ) : request.error ? (
        <Alert variant='danger'>
          {request.error}
          <Button variant='flat' onPress={() => void request.reload()}>
            {t('Reintentar')}
          </Button>
        </Alert>
      ) : (
        selected !== undefined && (
          <Autocomplete
            label={t('Entidad externa')}
            placeholder={t('Busca por nombre o tipo')}
            defaultItems={active}
            selectedKey={selected == null ? null : String(selected)}
            onSelectionChange={(key) =>
              onSelectionChange?.(key == null ? null : Number(key))
            }
            isDisabled={frozen}
            listboxProps={{
              emptyContent: t('Agrega una entidad para seleccionarla.'),
            }}
          >
            {(e) => (
              <AutocompleteItem
                key={String(e.id)}
                textValue={`${e.name} · ${e.entity_type}`}
                description={e.entity_type}
              >
                {e.name}
              </AutocompleteItem>
            )}
          </Autocomplete>
        )
      )}
      {managing && (
        <div
          className='flex flex-col gap-3 rounded-medium border border-default-200 p-4'
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <p className='text-sm text-default-500'>
            {t(
              'Entidades de tu exportadora. Los cambios no modifican solicitudes anteriores.'
            )}
          </p>
          <Input
            label={t('Nombre de la entidad')}
            value={name}
            onValueChange={setName}
            maxLength={200}
            isDisabled={frozen}
          />
          <Autocomplete
            label={t('Tipo de entidad')}
            placeholder={t('Escribe un tipo o elige uno anterior')}
            allowsCustomValue
            inputValue={entityType}
            selectedKey={
              (request.data?.types || []).includes(entityType)
                ? entityType
                : null
            }
            onInputChange={setEntityType}
            onSelectionChange={(key) => {
              if (key != null) setEntityType(String(key));
            }}
            defaultItems={(request.data?.types || []).map((type) => ({
              key: type,
            }))}
            isDisabled={frozen}
            listboxProps={{ emptyContent: t('Puedes escribir un tipo nuevo.') }}
          >
            {(type) => (
              <AutocompleteItem key={type.key} textValue={type.key}>
                {type.key}
              </AutocompleteItem>
            )}
          </Autocomplete>
          <div className='flex flex-wrap gap-2'>
            <Button
              size='sm'
              color='primary'
              isLoading={saving}
              isDisabled={
                frozen ||
                request.loading ||
                Boolean(request.error) ||
                !name.trim() ||
                !entityType.trim()
              }
              onPress={() => void save(name, entityType, editing)}
            >
              {t(editing ? 'Guardar entidad' : 'Agregar entidad')}
            </Button>
            {editing && (
              <Button
                size='sm'
                variant='light'
                isDisabled={frozen}
                onPress={() => {
                  setEditing(undefined);
                  setName('');
                  setEntityType('');
                }}
              >
                {t('Cancelar edición')}
              </Button>
            )}
          </div>
          {entities.map((e) => (
            <div
              key={e.id}
              className='flex flex-wrap items-center justify-between gap-2'
            >
              <div className='flex flex-wrap items-center gap-2'>
                <span
                  className={e.active ? 'text-sm' : 'text-sm text-default-500'}
                >
                  {e.name}
                </span>
                <Chip size='sm'>{e.entity_type}</Chip>
                {!e.active && (
                  <span className='text-xs text-default-500'>
                    {t('Inactivo')}
                  </span>
                )}
              </div>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='light'
                  aria-label={`${t('Editar entidad')}: ${e.name}`}
                  isDisabled={frozen || request.loading}
                  onPress={() => {
                    setEditing(e);
                    setName(e.name);
                    setEntityType(e.entity_type);
                  }}
                >
                  {t('Editar')}
                </Button>
                <Button
                  size='sm'
                  variant='light'
                  aria-label={`${t(e.active ? 'Desactivar entidad' : 'Activar entidad')}: ${e.name}`}
                  isDisabled={frozen || request.loading}
                  onPress={() => void save(e.name, e.entity_type, e, !e.active)}
                >
                  {t(e.active ? 'Desactivar' : 'Activar')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
    </section>
  );
}
